import { getGroupsByUserId } from 'api/operations/groupService';
import axios from 'axios';
import { GroupContextMoreInfoBlobInput } from 'components/common/group/GroupContextMoreInfoBlob';
import { bpHistory } from 'global/bpHistory';
import { buildGroupContactInfoMap } from 'global/services-helpers/groupService';
import { makeAutoObservable, runInAction } from 'mobx';
import { generatePath } from 'react-router-dom';
import { RouteV2Id, ROUTES_CONFIG_V2 } from 'routes/routesConfigV2';
import { RootStore } from 'store/rootStore';
import { Group } from 'types/groupService';
import { COMMON_URLS } from '@insurancemenu/imenu-components-lib/dist/utils/urls';

type AllowedRedirectRoute = typeof RouteV2Id.EMPLOYEE_PROFILE | typeof RouteV2Id.EMPLOYER_PROFILE;

function redirectToProfile(routeId: AllowedRedirectRoute, groupId: string): void {
  bpHistory.push(generatePath(ROUTES_CONFIG_V2[routeId].path, groupId ? { groupId } : {}));
}

async function getNewAuthToken(groupId: string): Promise<void> {
  await axios.post(
    `https://${COMMON_URLS.api}/aaa/Prod/setSelfEnrollAffiliation`,
    {
      groupId,
    },
    { withCredentials: true },
  );
}

export class SubscriberMultiLoginModule {
  private _rootStore: RootStore;
  private _activeGroups: Group[] = [];
  private _activeGroup: Group | null = null;

  public get activeGroups(): Group[] {
    return this._activeGroups;
  }

  public get activeGroup(): Group | null {
    return this._activeGroup;
  }

  public get groupsWhereUserIsEmployee(): Group[] {
    return this._activeGroups.filter((group) => group.seUserRole === 'EMPLOYEE');
  }

  public get groupsWhereUserIsEmployer(): Group[] {
    return this._activeGroups.filter((group) => group.seUserRole === 'EMPLOYER');
  }

  public get moreInfoBlobInput(): GroupContextMoreInfoBlobInput | null {
    if (!this._activeGroup) return null;

    return {
      blobTitle: this._activeGroup.name,
      generalInfoMap: {
        group: this._activeGroup.name,
      },
      contactInfoMap: buildGroupContactInfoMap(this._activeGroup.contactInfo),
    };
  }

  public setActiveGroupById(groupId: string): boolean {
    const group = this._activeGroups.find(({ id }) => id === groupId);
    if (!group) return false;
    this._activeGroup = group;
    return true;
  }

  public async getGroupsList(): Promise<void> {
    const groups = await getGroupsByUserId();
    runInAction(() => {
      this._activeGroups = groups;
    });
  }

  /**
   * Prefer the group from the URL when present (login into that affiliation without
   * pushing history again). Otherwise pick a default by role and redirect onto its profile.
   */
  public async findActiveGroupAndRedirect(preferredGroupId?: string): Promise<void> {
    if (preferredGroupId && this.setActiveGroupById(preferredGroupId)) {
      await getNewAuthToken(preferredGroupId);
      await this._rootStore.userStore.updateUserInfoQuietly();
      return;
    }

    const currentRole = this._rootStore.userStore.userRoles[0];

    if (currentRole === 'EMPLOYEE') {
      if (this.groupsWhereUserIsEmployee.length > 0) {
        await this.reLoginIntoActiveGroup(this.groupsWhereUserIsEmployee[0].id, RouteV2Id.EMPLOYEE_PROFILE);
      } else if (this.groupsWhereUserIsEmployer.length > 0) {
        await this.reLoginIntoActiveGroup(this.groupsWhereUserIsEmployer[0].id, RouteV2Id.EMPLOYER_PROFILE);
      }
      return;
    }

    if (currentRole === 'EMPLOYER') {
      if (this.groupsWhereUserIsEmployer.length > 0) {
        await this.reLoginIntoActiveGroup(this.groupsWhereUserIsEmployer[0].id, RouteV2Id.EMPLOYER_PROFILE);
      } else if (this.groupsWhereUserIsEmployee.length > 0) {
        await this.reLoginIntoActiveGroup(this.groupsWhereUserIsEmployee[0].id, RouteV2Id.EMPLOYEE_PROFILE);
      }
    }
  }

  public async reLoginIntoActiveGroup(activeGroupId: string, redirectRoute: AllowedRedirectRoute): Promise<void> {
    await getNewAuthToken(activeGroupId);
    this.setActiveGroupById(activeGroupId);
    await this._rootStore.userStore.updateUserInfoQuietly();
    redirectToProfile(redirectRoute, activeGroupId);
  }

  constructor(rootStore: RootStore) {
    this._rootStore = rootStore;
    makeAutoObservable<SubscriberMultiLoginModule, '_rootStore'>(this, { _rootStore: false }, { autoBind: true });
  }
}
